<template>
  <div class="notification">
    <!-- Card Header -->
    <div class="card">
      <div class="card-header">
        <i
          v-if="notificationIcon"
          class="icon fa-5x"
          :class="notificationIcon"
        ></i>
        <div class="card-content">
          <div class="card-title">
            <div v-if="isZurichEmbed" class="title-main">
              <span v-if="timeCreatedAt" class="title time-title">{{ timeCreatedAt }}</span>
              <p v-if="item.subject" class="title">{{ item.subject }}</p>
            </div>
            <p v-else-if="item.subject" class="title">{{ item.subject }}</p>
            <div class="title-aside">
              <span v-if="!isZurichEmbed && timeCreatedAt" class="title">
                {{ timeCreatedAt }}
              </span>
              <button
                v-if="isAppointment && !isCancelled"
                class="appointment-button join-button"
                :disabled="!appointmentId"
                @click="joinAppointment"
              >
                Join
              </button>
            </div>
          </div>
          <div class="card-time-action">
            <p v-if="shortDescription" class="subtitle">
              {{ shortDescription }}
            </p>
            <p
              v-if="!shortDescription"
              class="subtitle"
              v-html="notificationBody"
            ></p>
            <template v-if="shortDescription && isExpanded">
              <button
                class="close-icon"
                v-if="notificationBody"
                @click="isExpanded = !isExpanded"
              >
                <font-awesome-icon :icon="['fa', 'xmark']" class="icon-x" />
              </button>
            </template>
            <template v-else-if="shortDescription">
              <button
                class="view-button"
                v-if="notificationBody"
                @click="isExpanded = !isExpanded"
              >
                <span class="button-text">View</span>
                <span class="icon-box">
                  <font-awesome-icon
                    class="icon-small"
                    icon="arrow-right"
                    size="md"
                  />
                </span>
              </button>
            </template>
          </div>
          <div v-if="isReminder" class="reminder-notice">
            <p>
              For both audio appointments and video appointments, you will need to
              return to the app to join your session at the scheduled time.
            </p>
            </br>
            <p>
              Clinicians do not call users directly. Ensure you log in a few minutes
              before your appointment to avoid missing your session.
            </p>
            <p>
              If you are experiencing any issues with your appointment, please
              contact
              <a :href="`mailto:${supportEmail}`">{{ supportEmail }}</a>
            </p>
          </div>
        </div>
      </div>
      <transition
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div v-show="isExpanded" ref="expandable" class="expanded-content">
          <div class="subtitle" v-html="notificationBody"></div>
        </div>
      </transition>
      <div v-if="isAppointment" class="appointment-footer">
        <template v-if="cancelMode">
          <button
            class="appointment-button outline-button"
            @click="cancelMode = false"
          >
            Go back
          </button>
          <button
            class="appointment-button danger-button"
            :disabled="cancellationInProgress"
            @click="cancelAppointment"
          >
            {{ cancellationInProgress ? "Cancelling…" : "Yes, cancel" }}
          </button>
        </template>
        <button
          v-else
          class="appointment-button"
          :disabled="!appointmentId || isCancelled"
          @click="cancelMode = true"
        >
          {{ isCancelled ? "Cancelled" : "Cancel Session" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/config/constants";
import EventBus from "@/helpers/eventbus";
import { toDisplayMoment } from "@/utils/dateTime/dateTime";
import { getSupportEmail } from "@/utils/supportEmail/supportEmail";
import { isZurichEmbed } from "@/utils/embed/embed";

export default {
  name: "NotificationItem",
  props: {
    item: Object
  },
  data() {
    return {
      // Join/Cancel and the stacked time/subject header are Zurich-embed only.
      isZurichEmbed: isZurichEmbed(),
      isExpanded: false,
      cancelMode: false,
      cancellationInProgress: false,
      isCancelled: false
    };
  },
  computed: {
    supportEmail() {
      return getSupportEmail();
    },
    isReminder() {
      return this.item?.type === "SalesforceBookingReminder";
    },
    // Actions stay available until 15 minutes after the start, so a member who
    // is running late can still join.
    isAppointment() {
      if (!this.isZurichEmbed) return false;
      if (this.item?.type !== "SalesforceBookingReminder") return false;
      if (this.item?.appointmentStatus === "Cancelled") return false;
      const booking = this.item?.event?.salesforce_booking_confirmation || {};
      const start = booking.start_date_time_utc || booking.start_date_time;
      if (!start) return true;
      return Date.now() < new Date(start).getTime() + 15 * 60 * 1000;
    },
    // SalesforceBooking notifications carry the Salesforce appointment id under
    // event.salesforce_booking_confirmation.
    appointmentId() {
      return (
        this.item?.event?.salesforce_booking_confirmation?.sf_appointment_id ||
        null
      );
    },
    shortDescription() {
      const salesforceKey = Object.keys(this.item?.event).find(key =>
        key.startsWith("vpc_")
      );
      if (salesforceKey && this.item?.event[salesforceKey]?.short_description) {
        return this.item?.event[salesforceKey]?.short_description
      } else {
        return null
      }
    },
    dateCreatedAt() {
      // createdAt is a UTC instant, so read its day in the member's zone.
      const parsed = toDisplayMoment(this.item?.createdAt);
      return parsed ? parsed.format("YYYY-MM-DD") : "";
    },
    timeCreatedAt() {
      const parsed = toDisplayMoment(this.item?.createdAt);
      return parsed ? parsed.format("h:mma") : "";
    },
    notificationBody() {
      const saleforceFileBody = this.item?.event?.vpc_salesforce_file
        ?.base_64_body;

      return saleforceFileBody
        ? this.decodeData(saleforceFileBody)
        : this.item.body;
    },
    notificationIcon() {
      const salesforceKey = Object.keys(this.item?.event).find(key =>
        key.startsWith("vpc_salesforce")
      );
      const iconValue = salesforceKey
        ? this.item?.event[salesforceKey].icon
        : "fa fa-bell";
      return iconValue;
    }
  },
  methods: {
    joinAppointment() {
      if (!this.appointmentId) return;
      this.$router.push("/session/dmh/" + this.appointmentId);
    },
    async cancelAppointment() {
      if (this.cancellationInProgress || !this.appointmentId) return;
      this.cancellationInProgress = true;
      try {
        const res = await axios.post(
          config.api_env + "/application/api/u/dmh/cancel-appointment",
          {
            user_id: this.$store.getters.user_id,
            appointment_id: this.appointmentId,
            message: "User cancelled the session"
          },
          config.options
        );
        if (!res.data.success) throw new Error("cancel-appointment refused");
        this.isCancelled = true;
        EventBus.$emit("alert", {
          message: "Your session has been cancelled",
          success: true
        });
      } catch {
        EventBus.$emit("alert", {
          message:
            "An error occurred while canceling your session. Please try again later.",
          success: false
        });
      } finally {
        this.cancellationInProgress = false;
        this.cancelMode = false;
      }
    },
    decodeData(data) {
      try {
        return atob(data);
      } catch (error) {
        return data;
      }
    },
    beforeEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
      el.style.overflow = "hidden";
    },
    enter(el) {
      const height = el.scrollHeight;
      el.style.transition = "none";

      // Force reflow to apply height 0
      void el.offsetHeight;

      el.style.transition = "height 0.4s ease, opacity 0.4s ease";
      el.style.height = height + "px";
      el.style.opacity = "1";
    },
    afterEnter(el) {
      el.style.height = "auto";
      el.style.transition = "";
      el.style.overflow = "";
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
      el.style.overflow = "hidden";
    },
    leave(el) {
      el.style.transition = "height 0.4s ease, opacity 0.4s ease";

      // Force reflow again
      void el.offsetHeight;

      el.style.height = "0";
      el.style.opacity = "0";
    },
    afterLeave(el) {
      el.style.transition = "";
      el.style.overflow = "";
    }
  }
};
</script>

<style scoped lang="less">
.notification {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 8px 0 18px;
}

.card {
  border-radius: 8px;
  padding: 32px;
  border: 1px solid rgba(224, 229, 245, 1);
  box-shadow: 0 2px 16px 0 rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.card-header {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 30px;
  padding: 0;
  background-color: transparent;
  border: none;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.icon {
  align-self: flex-start;
  margin: 1px;
  color: var(--brand-primary);
}

.title {
  font-weight: 500;
  font-size: 20px;
  line-height: 30px;
  color: var(--brand-primary) !important;
  margin: 0;
  width: auto;

  &.time-title {
    color: #23366F !important;
  }
}

.subtitle {
  font-weight: 400;
  font-size: 22px;
  line-height: 30px;
  color: #23366F !important;
  margin: 0 0 10px;
  max-width: 90%;
}

.title-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.title-aside {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: none;
}

.appointment-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}

.appointment-button {
  font-weight: 700;
  min-width: 160px;
  padding: 16px 20px;
  // Same colour as the fill, so outlined variants keep the same size.
  border: 1px solid var(--brand-primary);
  border-radius: 40px;
  background: var(--brand-primary);
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.outline-button {
  background: #fff;
  color: var(--brand-primary);
}

.danger-button {
  border-color: #c62828;
  background: #c62828;
  color: #fff;
}

.text-content {
  display: flex;
  width: 100%;
}

.card-time-action {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  justify-content: space-between;
  width: 100%;
}

.time {
  font-size: 0.75rem;
  color: #999;
}

.reminder-notice {
  border-top: 2px solid var(--brand-primary);
  margin-top: 20px;
  padding-top: 20px;
  font-weight: 400;
  font-size: 22px;
  line-height: 30px;
  color: #122130;

  p {
    margin: 0 0 8px;
    color: #23366F !important;
  }

  p:last-child {
    margin-bottom: 0;
  }

  a {
    color: var(--brand-primary);
    text-decoration: underline;
  }
}

.view-button {
  height: 42px;
  width: 100px;
  flex: 0 0 100px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--brand-primary);
  color: white;
  font-weight: bold;
  padding: 9px 13px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.25rem;
}

.button-text {
  font-size: 1.6rem;
  margin-right: 16px;
}

.icon-box {
  background-color: white;
  padding: 5px 6px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-small {
  width: 11px;
  height: 11px;
  margin: 1px;
  color: var(--brand-primary);
}

.icon-x {
  width: 25px;
  height: 25px;
  color: #122130;
}

.close-icon {
  height: 42px;
  width: 100px;
  padding: 0;
  background: transparent;
  border: none;
  flex: 0 0 100px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.expanded-content {
  border-top: 2px solid var(--brand-primary);
  padding-top: 20px;
  margin-top: 20px;
  font-size: 0.875rem;
  color: #333;
}

.expanded-content ul {
  padding-left: 1rem;
  margin: 0.5rem 0;
}

.expanded-content ul li {
  margin-bottom: 0.4rem;
}
</style>
